import { useEffect, useState, useCallback, FormEvent, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Ruler, MapPin, Share2, ChevronLeft, ChevronRight, X, Check, TrendingUp } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import ContentSection from "@/components/sections/ContentSection";
import CTABlock from "@/components/sections/CTABlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { sendContactEmails } from "@/lib/contactEmails";
import drListings, { type DRListing } from "@/data/drListings";
import { useProperties } from "@/hooks/useProperties";
import NotFound from "./NotFound";

const Lightbox = ({
  images, index, onClose, onPrev, onNext,
}: {
  images: string[]; index: number; onClose: () => void; onPrev: () => void; onNext: () => void;
}) => (
  <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={onClose}>
    <button onClick={onClose} className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors" aria-label="Close">
      <X size={28} />
    </button>
    <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 md:left-8 text-white/70 hover:text-white transition-colors" aria-label="Previous">
      <ChevronLeft size={36} />
    </button>
    <img src={images[index]} alt={`Photo ${index + 1}`} className="max-h-[85vh] max-w-[90vw] object-contain" onClick={(e) => e.stopPropagation()} />
    <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 md:right-8 text-white/70 hover:text-white transition-colors" aria-label="Next">
      <ChevronRight size={36} />
    </button>
    <p className="absolute bottom-6 text-white/50 font-body text-sm tracking-widest">{index + 1} / {images.length}</p>
  </div>
);

const DRListingDetail = () => {
  const { slug } = useParams();
  const fallback = drListings.find((l) => l.slug === slug);

  // Try to resolve from Supabase first; if found, merge over fallback structure
  const { data: cmsRows, loading: cmsLoading } = useProperties({ slug, withImages: true, enabled: !!slug });
  const cms = cmsRows[0];

  const listing: DRListing | undefined = useMemo(() => {
    if (!cms) return fallback;
    // Build a DRListing-shaped object from Supabase, falling back to hardcoded for missing pieces
    const cmsImages = cms.images?.length ? cms.images.map((i) => i.image_url) : [];
    const heroImg = cms.hero_image_url ? [cms.hero_image_url] : [];
    const images = [...heroImg, ...cmsImages].filter(Boolean);
    const finalImages = images.length ? images : (fallback?.images ?? []);
    const description = cms.description
      ? cms.description.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
      : (fallback?.description ?? []);
    const features = cms.features?.length
      ? cms.features
      : cms.amenities?.length
      ? cms.amenities
      : (fallback?.features ?? []);
    return {
      slug: cms.slug,
      status: cms.listing_status ?? fallback?.status ?? "Available",
      tag: cms.listing_status ?? fallback?.tag ?? "",
      price: cms.price ?? fallback?.price ?? "Contact for pricing",
      priceNote: fallback?.priceNote,
      name: cms.title,
      location: cms.location ?? fallback?.location ?? "",
      type: cms.property_type ?? fallback?.type ?? "Property",
      beds: cms.bedrooms != null ? String(cms.bedrooms) : (fallback?.beds ?? "—"),
      baths: cms.bathrooms != null ? Number(cms.bathrooms) : (fallback?.baths ?? 0),
      sqft: cms.square_feet ? String(cms.square_feet) : (fallback?.sqft ?? ""),
      images: finalImages,
      description,
      features,
      details: fallback?.details ?? [],
      investment: fallback?.investment ?? {
        rentalYield: "Contact for details",
        occupancyRate: "Contact for details",
        estAnnualReturn: "Contact for details",
        nearbyAmenities: [],
      },
    };
  }, [cms, fallback]);


  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  const [inquiry, setInquiry] = useState({ name: "", email: "", phone: "", message: "" });
  const [inquiryError, setInquiryError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInquirySubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setInquiryError(null);

    const name = inquiry.name.trim();
    const email = inquiry.email.trim();
    const defaultMessage = `I'm interested in ${listing?.name ?? "this property"}. Please send me more details.`;
    const message = inquiry.message.trim() || defaultMessage;

    if (!name || !email) {
      setInquiryError("Please share your name and email so we can follow up.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setInquiryError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name,
        email,
        phone: inquiry.phone.trim() || null,
        interest: "Dominican Republic property",
        budget: null,
        preferred_location: listing?.location ?? "Dominican Republic",
        message,
      };

      const id = crypto.randomUUID();
      const { error } = await supabase
        .from("contact_submissions")
        .insert({ ...payload, id });
      if (error) throw error;

      await sendContactEmails(id, {
        ...payload,
        source: `Investment Inquiry — ${listing?.name ?? "DR Property"}`,
      });

      setIsSubmitted(true);
      setInquiry({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error("Inquiry submission failed", err);
      setInquiryError("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    const shareData = { title: listing?.name ?? "Property", url };
    if (navigator.share && navigator.canShare?.(shareData)) {
      try { await navigator.share(shareData); return; } catch { /* user cancelled */ }
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [listing?.name]);

  useEffect(() => {
    if (!listing) return;
    document.title = `${listing.name} | Hadassah Homes`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", `${listing.type} in ${listing.location}. ${listing.price}. ${listing.sqft} sqft. View photos and request investment details.`);
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `https://hadassahhomesinc.com/dominican-republic-investing/properties/${listing.slug}`;
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) {
      robots.setAttribute("content", "index, follow");
    }
  }, [listing]);

  if (!listing) {
    if (cmsLoading) {
      return (
        <SiteLayout>
          <div className="bg-secondary on-dark pt-32 md:pt-40 pb-32 text-center">
            <p className="font-body text-sm uppercase tracking-widest text-secondary-foreground/60">Loading…</p>
          </div>
        </SiteLayout>
      );
    }
    return <NotFound />;
  }

  const openLightbox = (i: number) => { setLightboxIndex(i); setLightboxOpen(true); };

  return (
    <SiteLayout>
      {lightboxOpen && (
        <Lightbox
          images={listing.images}
          index={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setLightboxIndex((p) => (p === 0 ? listing.images.length - 1 : p - 1))}
          onNext={() => setLightboxIndex((p) => (p === listing.images.length - 1 ? 0 : p + 1))}
        />
      )}

      {/* Breadcrumb */}
      <div className="bg-secondary on-dark pt-32 md:pt-40 pb-0">
        <div className="mx-auto max-w-wide px-5 md:px-12">
          <nav className="flex items-center gap-2 font-body text-xs uppercase tracking-widest text-secondary-foreground/50">
            <Link to="/dominican-republic-investing/properties" className="hover:text-accent transition-colors flex items-center gap-1.5">
              <ArrowLeft size={12} />
              Back to DR Properties
            </Link>
            <span>/</span>
            <span className="text-secondary-foreground/70">{listing.name}</span>
          </nav>
        </div>
      </div>

      {/* Photo Gallery */}
      <section className="bg-secondary on-dark pb-12 md:pb-16">
        <div className="mx-auto max-w-wide px-5 md:px-12 pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 lg:h-[520px] overflow-hidden">
            <button onClick={() => openLightbox(0)} className="lg:col-span-2 relative h-[300px] lg:h-full overflow-hidden group cursor-pointer">
              <img src={listing.images[activeImage]} alt={listing.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]" />
              <span className="absolute top-4 left-4 bg-accent text-accent-foreground font-body text-[11px] uppercase tracking-[0.2em] px-4 py-2">
                {listing.tag}
              </span>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
            </button>
            <div className="hidden lg:grid grid-rows-3 gap-2">
              {listing.images.slice(1, 4).map((img, i) => (
                <button key={i} onClick={() => openLightbox(i + 1)} className="relative overflow-hidden group cursor-pointer">
                  <img src={img} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
            {listing.images.map((img, i) => (
              <button
                key={i}
                onClick={() => { setActiveImage(i); openLightbox(i); }}
                className={cn(
                  "shrink-0 w-20 h-14 md:w-24 md:h-16 overflow-hidden border-2 transition-all",
                  activeImage === i ? "border-accent" : "border-transparent hover:border-secondary-foreground/30"
                )}
              >
                <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
            <button onClick={() => openLightbox(0)} className="shrink-0 w-20 h-14 md:w-24 md:h-16 bg-secondary-foreground/10 flex items-center justify-center font-body text-xs text-secondary-foreground/60 hover:text-accent transition-colors">
              View All
            </button>
          </div>
        </div>
      </section>

      {/* Key Details Strip */}
      <section className="bg-background border-b border-border">
        <div className="mx-auto max-w-wide px-5 md:px-12 py-8 md:py-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <p className="font-headline text-3xl md:text-4xl lg:text-5xl text-foreground">{listing.price}</p>
              {listing.priceNote && (
                <p className="font-body text-sm text-muted-foreground mt-0.5">{listing.priceNote}</p>
              )}
              <p className="font-body text-muted-foreground mt-1 flex items-center gap-1.5">
                <MapPin size={14} className="text-accent" />
                {listing.name} · {listing.location}
              </p>
            </div>
            <div className="flex flex-wrap gap-6 md:gap-10">
              <div className="flex items-center gap-2.5">
                <TrendingUp size={18} className="text-accent" />
                <div>
                  <p className="font-headline text-xl md:text-2xl text-foreground">{listing.investment.rentalYield}</p>
                  <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Rental Yield</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <Ruler size={18} className="text-accent" />
                <div>
                  <p className="font-headline text-xl md:text-2xl text-foreground">{listing.sqft}</p>
                  <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Sqft</p>
                </div>
              </div>
              <div>
                <p className="font-headline text-xl md:text-2xl text-foreground">{listing.beds}</p>
                <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Beds</p>
              </div>
              <div>
                <p className="font-headline text-xl md:text-2xl text-foreground">{listing.baths}</p>
                <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Baths</p>
              </div>
            </div>
            <div className="flex gap-3 relative">
              <button
                onClick={handleShare}
                className="p-3 border border-border hover:border-accent hover:text-accent transition-colors text-muted-foreground"
                aria-label="Share listing"
              >
                <Share2 size={18} />
              </button>
              {copied && (
                <span className="absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap bg-foreground text-background font-body text-xs px-3 py-1.5 rounded-sm pointer-events-none">
                  Link copied!
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Description + Sidebar */}
      <ContentSection>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-16">
            <div>
              <h2 className="font-headline text-2xl md:text-3xl text-foreground mb-6">About This Property</h2>
              <div className="space-y-5">
                {listing.description.map((para, i) => (
                  <p key={i} className="font-body text-muted-foreground leading-[1.75]">{para}</p>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-headline text-2xl md:text-3xl text-foreground mb-6">Property Details</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-5 gap-x-8 border-t border-border pt-6">
                {listing.details.map((detail) => (
                  <div key={detail.label}>
                    <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{detail.label}</p>
                    <p className="font-body text-foreground font-medium">{detail.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-border pt-8">
              <h2 className="font-headline text-2xl md:text-3xl text-foreground mb-6">Features & Amenities</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {listing.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3 py-2">
                    <Check size={16} className="text-accent mt-0.5 shrink-0" />
                    <span className="font-body text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-border pt-8">
              <h2 className="font-headline text-2xl md:text-3xl text-foreground mb-6">Investment Snapshot</h2>
              <div className="grid grid-cols-3 gap-4 mb-8">
                {[
                  { label: "Rental Yield", value: listing.investment.rentalYield },
                  { label: "Avg Occupancy", value: listing.investment.occupancyRate },
                  { label: "Est. Annual Return", value: listing.investment.estAnnualReturn },
                ].map((metric) => (
                  <div key={metric.label} className="border border-border p-5 text-center">
                    <p className="font-headline text-2xl text-accent">{metric.value}</p>
                    <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{metric.label}</p>
                  </div>
                ))}
              </div>

              <div>
                <h3 className="font-body text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-4">Nearby</h3>
                <div className="space-y-3">
                  {listing.investment.nearbyAmenities.map((item) => (
                    <div key={item.name} className="flex items-center justify-between py-3 border-b border-border/60">
                      <p className="font-body text-foreground">{item.name}</p>
                      <p className="font-body text-xs text-muted-foreground">{item.distance}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 space-y-8">
              <div className="border border-border bg-card p-8">
                <h3 className="font-headline text-xl text-foreground mb-1">Request Investment Details</h3>
                <p className="font-body text-sm text-muted-foreground mb-6">
                  Interested in this property? We'll send you pricing, floor plans, and availability within 24 hours.
                </p>
                {isSubmitted ? (
                  <div className="border border-accent/25 bg-accent/8 px-5 py-6">
                    <p className="font-headline text-lg text-foreground">Thank you</p>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      We received your request and will follow up within 24 hours with pricing, floor plans, and availability.
                    </p>
                  </div>
                ) : (
                  <form className="space-y-4" onSubmit={handleInquirySubmit}>
                    <div>
                      <label htmlFor="dr-name" className="sr-only">Full Name</label>
                      <Input
                        id="dr-name"
                        placeholder="Full Name"
                        className="h-12"
                        value={inquiry.name}
                        onChange={(e) => setInquiry({ ...inquiry, name: e.target.value })}
                        maxLength={100}
                      />
                    </div>
                    <div>
                      <label htmlFor="dr-email" className="sr-only">Email</label>
                      <Input
                        id="dr-email"
                        type="email"
                        placeholder="Email Address"
                        className="h-12"
                        value={inquiry.email}
                        onChange={(e) => setInquiry({ ...inquiry, email: e.target.value })}
                        maxLength={255}
                      />
                    </div>
                    <div>
                      <label htmlFor="dr-phone" className="sr-only">Phone</label>
                      <Input
                        id="dr-phone"
                        type="tel"
                        placeholder="Phone Number"
                        className="h-12"
                        value={inquiry.phone}
                        onChange={(e) => setInquiry({ ...inquiry, phone: e.target.value })}
                        maxLength={40}
                      />
                    </div>
                    <div>
                      <label htmlFor="dr-message" className="sr-only">Message</label>
                      <Textarea
                        id="dr-message"
                        placeholder={`I'm interested in ${listing.name}. Please send me more details.`}
                        className="min-h-[120px] resize-none"
                        value={inquiry.message}
                        onChange={(e) => setInquiry({ ...inquiry, message: e.target.value })}
                        maxLength={2000}
                      />
                    </div>
                    {inquiryError ? (
                      <p className="text-xs text-destructive">{inquiryError}</p>
                    ) : null}
                    <Button
                      variant="gold"
                      size="lg"
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full gap-2"
                    >
                      {isSubmitting ? "Sending..." : "Send Request"}
                      <ArrowRight size={14} />
                    </Button>
                  </form>
                )}
                <p className="font-body text-[10px] text-muted-foreground mt-4 text-center">
                  By submitting, you agree to our{" "}
                  <Link to="/privacy-policy" className="underline hover:text-accent transition-colors">Privacy Policy</Link>.
                </p>
              </div>

              <div className="border border-border bg-card p-8 text-center">
                <p className="font-body text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-3">Prefer to Talk?</p>
                <a href="tel:+17816323936" className="font-headline text-xl text-foreground hover:text-accent transition-colors">
                  (781) 632-3936
                </a>
                <p className="font-body text-xs text-muted-foreground mt-2">
                  <a href="mailto:Info@hadassahhomesinc.com" className="hover:text-accent transition-colors">
                    Info@hadassahhomesinc.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      <CTABlock
        title="Ready to Learn More?"
        description="Book a consultation and we'll walk you through this property and others that match your investment goals."
        primaryCta={{ text: "Book a Consultation", href: "/consultation" }}
        variant="dark"
      />
    </SiteLayout>
  );
};

export default DRListingDetail;
